import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Waves, HeartHandshake, CalendarDays } from 'lucide-react';
import { siteInfo, programsHeroImage } from '../data/mock';
import SessionCountdown from '../components/home/SessionCountdown';
import FindBestFitButton from '../components/FindBestFitButton';
import { useQuiz } from '../context/QuizProvider';
import ProgramRouteCards from '../components/programs/ProgramRouteCards';

export default function Programs() {
  const { open: openQuiz } = useQuiz();
  return (
    <main>
      <SessionCountdown variant="compact" />
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Winter 2026&ndash;27 &middot; Monthly Memberships</div>
            <h1 className="font-display font-bold text-4xl md:text-6xl text-splash-green leading-tight">Every age has a <span className="text-splash-teal">clear path.</span></h1>
            <p className="mt-6 text-splash-green/80 text-lg leading-relaxed max-w-xl">
              Choose your child&rsquo;s age below to see exactly what the program includes, what it costs, and how to enroll. Not sure? Our quick quiz will point you in the right direction.
            </p>
            <div className="mt-8"><FindBestFitButton onClick={openQuiz} dataTestId="programs-best-fit-btn" /></div>
          </div>
          <div className="relative">
            <img src={programsHeroImage} alt="Child at the pool wall with goggles and gear, ready for swim class" loading="lazy" decoding="async" className="w-full h-[400px] object-cover rounded-[2rem] shadow-xl" />
            <div className="absolute bottom-6 left-6 bg-white rounded-2xl px-5 py-4 shadow-xl border border-splash-sand max-w-[260px]">
              <CalendarDays className="w-5 h-5 text-splash-teal" />
              <div className="mt-2 font-semibold text-splash-green text-sm">Enrollment opens Oct 30, 2026</div>
              <div className="text-xs text-splash-green/70">Monthly memberships &middot; no sessions</div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-8" data-testid="programs-routes">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-splash-green mb-8">Programs by age</h2>
          <ProgramRouteCards testIdPrefix="programs-route-card" />
        </div>
      </section>

      <section className="py-20" data-testid="programs-why-families">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Why Families Choose Little Splash</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-splash-green">Swim lessons designed around <span className="text-splash-teal">your</span> child</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Waves, title: 'Private Heated Pool', desc: 'One class in the water at a time keeps your child focused and comfortable.' },
              { icon: HeartHandshake, title: 'Confidence-First', desc: 'No fear, no pressure. We meet every child exactly where they are.' },
              { icon: MapPin, title: 'Des Moines, IA', desc: `We operate primarily at ${siteInfo.address}.` },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-3xl p-8 border border-splash-sand">
                <Icon className="w-8 h-8 text-splash-teal" />
                <h3 className="mt-4 font-display font-bold text-2xl text-splash-green">{title}</h3>
                <p className="mt-3 text-splash-green/75 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="How-to-Enroll" className="py-16 bg-splash-creamDark">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Get Started</div>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-splash-green">Three simple steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: '1', title: 'Pick your age route', body: 'First Splash (8–12 months), Little Splash Foundations™ (ages 1–3), Group Membership (3–12 years), or Private / Semi-Private.' },
              { num: '2', title: 'Enroll for that program', body: 'Each program has its own enrollment: book First Splash on its calendar, join the Little Splash Foundations™ waitlist, or start a monthly group membership per child.' },
              { num: '3', title: 'Questions? Talk to us', body: 'Book a free pre-enrollment call or a 20-minute swim assessment ($25) with Coach Abrie to make sure it’s the right fit.' },
            ].map((s) => (
              <div key={s.num} className="bg-white rounded-3xl p-8 border border-splash-sand">
                <div className="w-12 h-12 rounded-full bg-splash-teal text-white grid place-items-center font-display font-bold text-xl">{s.num}</div>
                <h3 className="mt-4 font-display font-bold text-2xl text-splash-green">{s.title}</h3>
                <p className="mt-3 text-splash-green/80 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/book" data-testid="programs-book-preenroll-btn" className="inline-flex items-center gap-2 rounded-full bg-splash-green hover:bg-splash-greenDark text-white px-7 py-3.5 font-semibold btn-transition">
              Book Pre-Enrollment Call <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/book?type=assessment" data-testid="programs-book-assessment-btn" className="inline-flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-7 py-3.5 font-semibold btn-transition">
              Book Swim Assessment <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
